/**
 * Une fonction par point d'entrée de l'API, typée avec les DTO de
 * `@portail/shared`. Aucun composant n'appelle `fetch` directement : la forme
 * exacte du contrat vit ici et nulle part ailleurs.
 */

import type {
  AdminOverviewDto,
  AppStatus,
  AppSummaryDto,
  AppVersionDto,
  AuditEntryDto,
  CheckSlugResultDto,
  CurrentUserDto,
  DeletionRequestDto,
  DeploymentDetailDto,
  DeploymentLogDto,
  DeploymentState,
  DeploymentSummaryDto,
  DomainDto,
  PageDto,
  QuotaDto,
  QuotaKey,
  Role,
  StatsPeriod,
  SystemHealthDto,
  VersionDiffDto,
  VersionDiffSideDto,
  VisitStatsDto,
} from '@portail/shared';

import { downloadFile, request } from './client';

// ---------------------------------------------------------------------------
// Enveloppes composées à partir des DTO partagés
// ---------------------------------------------------------------------------

/** `GET /api/me` : le profil, augmenté des quotas effectifs de l'utilisateur. */
export type CurrentUserResponse = CurrentUserDto & {
  quotas: Record<QuotaKey, QuotaDto>;
};

/** `GET /api/settings` : réglages serveur pilotant l'affichage (§3.2). */
export interface SettingsResponse {
  confirmationText: string;
  allowedDomains: string[];
  userCanDelete: boolean;
}

/** `POST /api/deployments` et `POST /api/apps/:id/redeploy` : réponse 202. */
export interface DeploymentAcceptedResponse {
  jobId: string;
  url: string;
}

/** `POST /api/apps/:id/delete-request`. */
export interface DeleteRequestResponse {
  ok: boolean;
  /** Vrai lorsque `USER_CAN_DELETE` est activé : la suppression est immédiate. */
  immediate: boolean;
  message: string;
}

/** `GET /api/admin/apps/:id` : fiche détaillée (§6.3). */
export interface AdminAppDetailResponse {
  app: AppSummaryDto;
  deployments: DeploymentSummaryDto[];
  stats: VisitStatsDto | null;
}

/** `GET /api/admin/users` : liste des comptes connus du portail. */
export interface AdminUserResponse {
  id: string;
  email: string;
  displayName: string;
  role: Role;
  createdAt: string;
  lastLoginAt: string | null;
  appCount: number;
  deploymentCount: number;
}

/** `GET /api/admin/system` : santé, augmentée des informations propres à l'API. */
export type AdminSystemResponse = SystemHealthDto & {
  databaseReachable: boolean;
  queueName: string;
};

/** Surcharge de quota appliquée à un utilisateur (§6.5). */
export interface QuotaOverrideResponse {
  userId: string | null;
  user: { id: string; email: string; displayName: string } | null;
  key: QuotaKey;
  value: number;
}

/** `GET /api/admin/quotas`. */
export interface QuotasResponse {
  defaults: Record<QuotaKey, number>;
  global: Record<QuotaKey, QuotaDto>;
  overrides: QuotaOverrideResponse[];
}

/** Une modification de quota transmise à `PUT /api/admin/quotas`. */
export interface QuotaUpdatePayload {
  scope: 'global' | 'user';
  userId?: string | null;
  key: QuotaKey;
  /** `null` retire la surcharge et rétablit la valeur héritée. */
  value: number | null;
}

/**
 * Filtres de l'inventaire des applications (§6.2).
 *
 * Déclarés en alias de type et non en interface : seuls les alias reçoivent
 * une signature d'index implicite, indispensable pour les passer tels quels au
 * constructeur de chaîne de requête.
 */
export type AdminAppsQueryParams = {
  page?: number;
  pageSize?: number;
  search?: string;
  status?: AppStatus | '';
  ownerId?: string;
  domain?: string;
  includeDeleted?: boolean;
  /** Corbeille : ne rend que les applications supprimées. */
  onlyDeleted?: boolean;
  /** Ne rend que les applications dont la suppression est demandée. */
  pendingDeletion?: boolean;
  sortBy?: 'createdAt' | 'slug' | 'lastDeployedAt' | 'visits7d';
  sortDir?: 'asc' | 'desc';
};

/** Filtres de la liste de tous les déploiements. */
export type AdminDeploymentsQueryParams = {
  page?: number;
  pageSize?: number;
  state?: DeploymentState | '';
  userId?: string;
  appId?: string;
  search?: string;
};

/** Filtres du journal d'audit (§6.4). */
export type AdminAuditQueryParams = {
  page?: number;
  pageSize?: number;
  actorId?: string;
  action?: string;
  targetType?: string;
  targetId?: string;
  from?: string;
  to?: string;
};

// ---------------------------------------------------------------------------
// Session et réglages
// ---------------------------------------------------------------------------

export function getSettings(): Promise<SettingsResponse> {
  return request<SettingsResponse>('/api/settings');
}

export function getCurrentUser(): Promise<CurrentUserResponse> {
  return request<CurrentUserResponse>('/api/me');
}

export function logout(): Promise<{ ok: boolean }> {
  return request<{ ok: boolean }>('/api/auth/logout', { method: 'POST' });
}

// ---------------------------------------------------------------------------
// Formulaire de déploiement (§3.2)
// ---------------------------------------------------------------------------

export function getDomains(): Promise<DomainDto[]> {
  return request<DomainDto[]>('/api/domains');
}

/**
 * Disponibilité d'un slug. `signal` permet d'annuler la vérification
 * précédente : sans cela, une réponse lente écraserait une réponse plus récente.
 */
export function checkSlug(
  slug: string,
  domain: string,
  signal?: AbortSignal,
): Promise<CheckSlugResultDto> {
  return request<CheckSlugResultDto>('/api/apps/check-slug', {
    query: { slug, domain },
    signal,
  });
}

/**
 * Crée un déploiement à partir d'un code collé **ou** d'une description.
 *
 * Exactement l'un des deux : le serveur refuse les deux à la fois, faute de pouvoir
 * les départager, et n'accepte pas non plus qu'ils manquent tous les deux.
 */
export function createDeployment(payload: {
  code?: string;
  description?: string;
  slug: string;
  domain: string;
  comment: string | null;
  /** Exige une connexion pour ouvrir l'application ; décidé à la création (§5.2). */
  forceSso?: boolean;
  confirmed: true;
}): Promise<DeploymentAcceptedResponse> {
  return request<DeploymentAcceptedResponse>('/api/deployments', {
    method: 'POST',
    body: payload,
  });
}

// ---------------------------------------------------------------------------
// Mes applications (§3.4)
// ---------------------------------------------------------------------------

export function listMyApps(): Promise<AppSummaryDto[]> {
  return request<AppSummaryDto[]>('/api/apps');
}

/** Fiche d'une application appartenant à l'utilisateur courant (§3.5). */
export function getApp(appId: string): Promise<AppSummaryDto> {
  return request<AppSummaryDto>(`/api/apps/${encodeURIComponent(appId)}`);
}

export function listAppDeployments(appId: string): Promise<DeploymentSummaryDto[]> {
  return request<DeploymentSummaryDto[]>(`/api/apps/${encodeURIComponent(appId)}/deployments`);
}

// ---------------------------------------------------------------------------
// Versions, comparaison et restauration (§3.5)
// ---------------------------------------------------------------------------

/** Historique des versions, du plus récent au plus ancien. */
export function listAppVersions(appId: string): Promise<AppVersionDto[]> {
  return request<AppVersionDto[]>(`/api/apps/${encodeURIComponent(appId)}/versions`);
}

/**
 * Comparaison de deux versions. Sans `against`, l'API compare la version
 * demandée à celle qui la précède immédiatement.
 */
export function getVersionDiff(
  appId: string,
  versionId: string,
  against?: string,
): Promise<VersionDiffDto> {
  return request<VersionDiffDto>(
    `/api/apps/${encodeURIComponent(appId)}/versions/${encodeURIComponent(versionId)}/diff`,
    { query: { against } },
  );
}

/**
 * Décision sur les changements de comportement annoncés par l'IA (§5.1).
 *
 * `accept: true` remet le job en file — le déploiement se poursuit sur le même
 * identifiant, la version garde son numéro. `accept: false` l'annule.
 */
export function reviewDeployment(
  deploymentId: string,
  accept: boolean,
): Promise<{ ok: boolean; accepted: boolean }> {
  return request<{ ok: boolean; accepted: boolean }>(
    `/api/deployments/${encodeURIComponent(deploymentId)}/review`,
    { method: 'POST', body: { accept } },
  );
}

/**
 * Renvoie des précisions à l'IA avant la mise en ligne (§5.1).
 *
 * Troisième voie de la validation, à côté d'accepter et d'abandonner : le
 * déploiement reprend sa transformation avec la remarque en plus, **sans changer
 * de numéro de version** — rien n'a été construit ni mis en ligne.
 */
export function refineDeployment(
  deploymentId: string,
  precisions: string,
): Promise<{ ok: boolean; refinements: number }> {
  return request<{ ok: boolean; refinements: number }>(
    `/api/deployments/${encodeURIComponent(deploymentId)}/refine`,
    { method: 'POST', body: { precisions } },
  );
}

/**
 * Nouvelle version décrite en langage naturel (§3.5).
 *
 * Aucun code n'est transmis : le point de départ est celui de la dernière version
 * aboutie, et la demande dit ce qui doit changer.
 */
export function modifyApp(
  appId: string,
  payload: {
    instruction: string;
    versionComment: string;
    comment: string | null;
    confirmed: true;
  },
): Promise<DeploymentAcceptedResponse> {
  return request<DeploymentAcceptedResponse>(`/api/apps/${encodeURIComponent(appId)}/modify`, {
    method: 'POST',
    body: payload,
  });
}

/**
 * Code d'une seule version.
 *
 * Sert à comparer un code **encore dans le formulaire** à une version déjà
 * déployée : la moitié gauche du comparatif vient du navigateur, seule celle de
 * droite est demandée au serveur.
 */
export function getVersionCode(appId: string, versionId: string): Promise<VersionDiffSideDto> {
  return request<VersionDiffSideDto>(
    `/api/apps/${encodeURIComponent(appId)}/versions/${encodeURIComponent(versionId)}/code`,
  );
}

/**
 * Nouvelle version à partir d'un nouveau code. Le commentaire de version est
 * obligatoire (§3.5) : sans lui, l'historique n'apprend rien à qui le consulte.
 */
export function redeployApp(
  appId: string,
  payload: {
    code: string;
    versionComment: string;
    comment: string | null;
    confirmed: true;
  },
): Promise<DeploymentAcceptedResponse> {
  return request<DeploymentAcceptedResponse>(`/api/apps/${encodeURIComponent(appId)}/redeploy`, {
    method: 'POST',
    body: payload,
  });
}

/** Restaure une version antérieure, sans reconstruction d'image (§5.5). */
export function rollbackApp(appId: string, versionId: string): Promise<DeploymentAcceptedResponse> {
  return request<DeploymentAcceptedResponse>(`/api/apps/${encodeURIComponent(appId)}/rollback`, {
    method: 'POST',
    body: { versionId },
  });
}

export function requestAppDeletion(
  appId: string,
  reason: string | null,
): Promise<DeleteRequestResponse> {
  return request<DeleteRequestResponse>(`/api/apps/${encodeURIComponent(appId)}/delete-request`, {
    method: 'POST',
    body: { reason },
  });
}

// ---------------------------------------------------------------------------
// Déploiements (§3.3)
// ---------------------------------------------------------------------------

export function getDeployment(id: string): Promise<DeploymentDetailDto> {
  return request<DeploymentDetailDto>(`/api/deployments/${encodeURIComponent(id)}`);
}

export function listMyDeployments(page = 1, pageSize = 25): Promise<PageDto<DeploymentSummaryDto>> {
  return request<PageDto<DeploymentSummaryDto>>('/api/deployments', {
    query: { page, pageSize },
  });
}

export function getDeploymentLogs(
  id: string,
  page = 1,
  pageSize = 200,
): Promise<PageDto<DeploymentLogDto>> {
  return request<PageDto<DeploymentLogDto>>(`/api/deployments/${encodeURIComponent(id)}/logs`, {
    query: { page, pageSize },
  });
}

export function cancelDeployment(id: string): Promise<{ ok: boolean }> {
  return request<{ ok: boolean }>(`/api/deployments/${encodeURIComponent(id)}/cancel`, {
    method: 'POST',
  });
}

export function retryDeployment(id: string): Promise<DeploymentAcceptedResponse> {
  return request<DeploymentAcceptedResponse>(`/api/deployments/${encodeURIComponent(id)}/retry`, {
    method: 'POST',
  });
}

/** URL du flux SSE, consommée par `EventSource` (§3.3). */
export function deploymentEventsUrl(id: string): string {
  return `/api/deployments/${encodeURIComponent(id)}/events`;
}

// ---------------------------------------------------------------------------
// Administration (§6)
// ---------------------------------------------------------------------------

export function getAdminOverview(): Promise<AdminOverviewDto> {
  return request<AdminOverviewDto>('/api/admin/stats/overview');
}

export function getAdminSystem(): Promise<AdminSystemResponse> {
  return request<AdminSystemResponse>('/api/admin/system');
}

export function listAdminUsers(): Promise<AdminUserResponse[]> {
  return request<AdminUserResponse[]>('/api/admin/users');
}

export function listAdminApps(params: AdminAppsQueryParams): Promise<PageDto<AppSummaryDto>> {
  return request<PageDto<AppSummaryDto>>('/api/admin/apps', {
    query: {
      ...params,
      // `z.coerce.boolean()` côté serveur transforme la chaîne « false » en
      // `true` : le drapeau doit être absent plutôt que faux.
      includeDeleted: params.includeDeleted ? true : undefined,
    },
  });
}

export function getAdminApp(id: string): Promise<AdminAppDetailResponse> {
  return request<AdminAppDetailResponse>(`/api/admin/apps/${encodeURIComponent(id)}`);
}

export function getAdminAppVisits(id: string, period: StatsPeriod): Promise<VisitStatsDto> {
  return request<VisitStatsDto>(`/api/admin/stats/apps/${encodeURIComponent(id)}/visits`, {
    query: { period },
  });
}

export function getAdminAppLogs(id: string, lines = 200): Promise<{ logs: string }> {
  return request<{ logs: string }>(`/api/admin/apps/${encodeURIComponent(id)}/logs`, {
    query: { lines },
  });
}

/** Actions de cycle de vie d'une application (§6.3). */
export function runAdminAppAction(
  id: string,
  action: 'start' | 'stop' | 'restart',
): Promise<{ ok: boolean }> {
  return request<{ ok: boolean }>(`/api/admin/apps/${encodeURIComponent(id)}/${action}`, {
    method: 'POST',
  });
}

/**
 * Restauration d'une version par un administrateur (§6.3). Même corps que la
 * route utilisateur, mais applicable à l'application de n'importe qui.
 */
export function rollbackAdminApp(
  id: string,
  versionId: string,
): Promise<DeploymentAcceptedResponse> {
  return request<DeploymentAcceptedResponse>(`/api/admin/apps/${encodeURIComponent(id)}/rollback`, {
    method: 'POST',
    body: { versionId },
  });
}

/**
 * Suppression : le nom exact de l'application est exigé (§6.3).
 *
 * Elle est logique — l'application part dans la corbeille, d'où elle peut être
 * restaurée ou effacée définitivement.
 *
 * `cloudronUninstalled` vaut faux lorsque la ligne a bien été marquée mais que
 * la plateforme, elle, n'a pas désinstallé l'application : celle-ci disparaît de
 * l'inventaire tout en continuant de tourner, ce qui doit être dit. `umamiDeleted`
 * joue le même rôle pour le site de mesure d'audience, qui continuerait sinon de
 * collecter pour une application disparue.
 */
export function deleteAdminApp(
  id: string,
  confirmSlug: string,
): Promise<{ ok: boolean; cloudronUninstalled: boolean; umamiDeleted: boolean }> {
  return request<{ ok: boolean; cloudronUninstalled: boolean; umamiDeleted: boolean }>(
    `/api/admin/apps/${encodeURIComponent(id)}/delete`,
    { method: 'POST', body: { confirmSlug } },
  );
}

/** Demandes de suppression en attente de décision : source des notifications. */
export function listDeletionRequests(): Promise<{ requests: DeletionRequestDto[] }> {
  return request<{ requests: DeletionRequestDto[] }>('/api/admin/deletion-requests');
}

/** Accepte une demande : la suppression est exécutée. */
export function approveDeletionRequest(
  id: string,
): Promise<{ ok: boolean; cloudronUninstalled: boolean; umamiDeleted: boolean }> {
  return request<{ ok: boolean; cloudronUninstalled: boolean; umamiDeleted: boolean }>(
    `/api/admin/apps/${encodeURIComponent(id)}/deletion-request/approve`,
    { method: 'POST' },
  );
}

/** Refuse une demande. Le motif est obligatoire : il est affiché au demandeur. */
export function refuseDeletionRequest(id: string, reason: string): Promise<AppSummaryDto> {
  return request<AppSummaryDto>(
    `/api/admin/apps/${encodeURIComponent(id)}/deletion-request/refuse`,
    { method: 'POST', body: { reason } },
  );
}

/**
 * Restaure une application supprimée.
 *
 * Elle redevient visible avec ses versions et leur code, et reprend son adresse —
 * mais n'est pas remise en ligne pour autant : c'est ensuite la restauration d'une
 * de ses versions qui la redéploie. Un 409 signale une adresse reprise depuis.
 */
export function restoreAdminApp(id: string): Promise<AppSummaryDto> {
  return request<AppSummaryDto>(`/api/admin/apps/${encodeURIComponent(id)}/restore`, {
    method: 'POST',
  });
}

/** Efface définitivement une application de la corbeille. Irréversible. */
export function purgeAdminApp(id: string, confirmSlug: string): Promise<{ ok: boolean }> {
  return request<{ ok: boolean }>(`/api/admin/apps/${encodeURIComponent(id)}/purge`, {
    method: 'POST',
    body: { confirmSlug },
  });
}

export function updateAdminApp(
  id: string,
  changes: { memoryLimitMb?: number; claudeProxyEnabled?: boolean },
): Promise<AppSummaryDto> {
  return request<AppSummaryDto>(`/api/admin/apps/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: changes,
  });
}

export function listAdminDeployments(
  params: AdminDeploymentsQueryParams,
): Promise<PageDto<DeploymentSummaryDto>> {
  return request<PageDto<DeploymentSummaryDto>>('/api/admin/deployments', {
    query: { ...params },
  });
}

export function getAdminDeployment(id: string): Promise<DeploymentDetailDto> {
  return request<DeploymentDetailDto>(`/api/admin/deployments/${encodeURIComponent(id)}`);
}

export function listAudit(params: AdminAuditQueryParams): Promise<PageDto<AuditEntryDto>> {
  return request<PageDto<AuditEntryDto>>('/api/admin/audit', {
    query: { ...params },
  });
}

export function listAuditActions(): Promise<string[]> {
  return request<string[]>('/api/admin/audit/actions');
}

export function getQuotas(): Promise<QuotasResponse> {
  return request<QuotasResponse>('/api/admin/quotas');
}

export function updateQuotas(updates: QuotaUpdatePayload[]): Promise<{ ok: boolean }> {
  return request<{ ok: boolean }>('/api/admin/quotas', { method: 'PUT', body: { updates } });
}

/** Déclenche le téléchargement d'un export (§6.2). */
export function downloadExport(
  dataset: 'apps' | 'deployments' | 'audit',
  format: 'csv' | 'json' = 'csv',
): void {
  downloadFile('/api/admin/export', { dataset, format });
}
